import {Checkbox} from "antd";
import React, {useMemo, useEffect} from "react";
import type {ColumnsWithKey} from "@/components/table/helpers.tsx";
import {useColumnVisibilityStore, useSelectedKeys} from "@/stores/ColumnVisibilityStore.ts";


/**
 * Return type for useColumnVisibility hook
 */
export type ColumnVisibilityResult<T> = {
  /** The visible columns (filtered) */
  visibleColumns: ColumnsWithKey<T>;
  /** Standalone component with checkbox toggles for column visibility */
  toggleComponent: React.ReactNode;
};

/**
 * Hook for column visibility switcher
 *
 * @param allColumns - All columns with unique keys
 * @returns Object with visible columns and standalone component
 */
export function useColumnVisibility<T extends object>(
    allColumns: ColumnsWithKey<T>,
    tableName: string,
): ColumnVisibilityResult<T> {
  // State for visible columns (all visible by default)
  const defaultAllSelectedKeys = useMemo(
    () => allColumns
      .filter((col) => !("children" in col))
      .map((col) => col.key as React.Key),
    [allColumns]
  );

  // Get store methods and selected keys
  const { initializeTable, setSelectedKeys } = useColumnVisibilityStore();
  const storeCheckedList = useSelectedKeys(tableName, defaultAllSelectedKeys);
  
  // Initialize table with defaults if not exists (runs once on mount)
  useEffect(() => {
    const initialized = initializeTable(tableName, defaultAllSelectedKeys);
    console.log("initialized", initialized);
  }, []);
  
  // Update store when checkedList changes
  const handleCheckedListChange = (newCheckedList: React.Key[]) => {
    setSelectedKeys(tableName, newCheckedList);
  };
  
  // Filter columns based on checkedList
  const visibleColumns = useMemo(
      () => {
        console.log("store checked list changed", storeCheckedList);
        return allColumns.filter((col) => storeCheckedList.includes(col.key as React.Key))
      },
      [storeCheckedList]
  );

  // Standalone component with checkbox toggles
  const toggleComponent = useMemo(
      () => (
          <div className="flex flex-col gap-2">
            {allColumns
            .filter((col) => typeof col.key === "string")
            .map((col) => {
              const colKey = col.key as string;
              return (
                  <Checkbox
                      key={colKey}
                      checked={storeCheckedList.includes(colKey)}
                      disabled={storeCheckedList.length === 1 && storeCheckedList.includes(colKey)} // At least one column must be visible
                      onChange={(e) => {
                        const newCheckedList = e.target.checked
                            ? [...storeCheckedList, colKey]
                            : storeCheckedList.filter((key) => key !== colKey);
                        // Ensure at least one column remains visible
                        if (newCheckedList.length > 0) {
                          handleCheckedListChange(newCheckedList);
                        }
                      }}
                  >
                    {typeof col.title === "function" ? "Column" : (col.title ?? "Column")}
                  </Checkbox>
              );
            })}
          </div>
      ),
      [allColumns, storeCheckedList, handleCheckedListChange]
  );

  return {
    visibleColumns,
    toggleComponent,
  };
}

